import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { X, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const storageKey = (id) => `popup_${id}`;

const RATIO_CSS = { '1:1': '1/1', '4:5': '4/5', '1.91:1': '191/100', '16:9': '16/9' };
const getRatio = (popup) => RATIO_CSS[popup.image_aspect_ratio] || '1/1';

const shouldShow = (popup) => {
  const stored = localStorage.getItem(storageKey(popup.id));
  if (!stored) return true;
  const { dismissedAt, permanent } = JSON.parse(stored);
  if (permanent) return false;
  const freqDays = popup.frequency_days ?? 1;
  if (freqDays === 0) return true;
  return (Date.now() - dismissedAt) / 86400000 >= freqDays;
};

const dismiss = (popup, permanent) => {
  localStorage.setItem(storageKey(popup.id), JSON.stringify({ dismissedAt: Date.now(), permanent }));
};

// ── Template renderers ────────────────────────────────────────

const CloseBtn = ({ onClick, dark }) => (
  <button onClick={onClick} className={`absolute top-3 right-3 z-10 rounded-full p-1.5 shadow transition-colors ${dark ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-white/90 hover:bg-white text-gray-600'}`}>
    <X className="h-4 w-4" />
  </button>
);

const FreqLabel = ({ popup, dontShow, setDontShow, dark }) => (
  <label className={`flex items-center gap-2 cursor-pointer ${dark ? 'text-white/70' : 'text-muted-foreground'}`}>
    <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} className="rounded" />
    <span className="text-xs">
      {popup.frequency_days === -1 ? '永久唔再顯示' : `${popup.frequency_days || 1} 日內唔再顯示`}
    </span>
  </label>
);

// 1. Classic — image auto-sizes to natural dimensions, popup resizes to fit
const ClassicTemplate = ({ popup, onClose, onBtn, dontShow, setDontShow, onImageLoad }) => {
  const img = resolveImageUrl(popup.image_url);

  const handleImgLoad = (e) => {
    if (onImageLoad) onImageLoad(e.target.naturalWidth, e.target.naturalHeight);
  };

  return (
    <div className="bg-white w-full overflow-hidden">
      <CloseBtn onClick={onClose} />
      {img && (
        <div className="w-full flex justify-center overflow-hidden bg-gray-50">
          <img
            src={img}
            alt=""
            onLoad={handleImgLoad}
            style={{
              width: 'auto',
              maxWidth: '100%',
              height: 'auto',
              maxHeight: 'min(65dvh, 65vh)',
              display: 'block',
            }}
          />
        </div>
      )}
      <div className="p-6 space-y-3">
        {popup.title && <h2 className="text-xl font-bold text-gray-900">{popup.title}</h2>}
        {popup.content && <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{popup.content}</p>}
        {popup.button_text && <Button className="w-full" onClick={onBtn}>{popup.button_text}</Button>}
        <FreqLabel popup={popup} dontShow={dontShow} setDontShow={setDontShow} />
      </div>
    </div>
  );
};

// 2. Offer — full image top, bold coloured header below
const OfferTemplate = ({ popup, onClose, onBtn, dontShow, setDontShow, onImageLoad }) => {
  const accent = popup.accent_color || '#2563eb';
  const img = resolveImageUrl(popup.image_url);

  const handleImgLoad = (e) => {
    if (onImageLoad) onImageLoad(e.target.naturalWidth, e.target.naturalHeight);
  };

  return (
    <div className="bg-white w-full overflow-hidden">
      <CloseBtn onClick={onClose} />
      {img && (
        <div className="w-full flex justify-center overflow-hidden bg-gray-50">
          <img src={img} alt="" onLoad={handleImgLoad}
            style={{ width: 'auto', maxWidth: '100%', height: 'auto', maxHeight: 'min(55dvh, 55vh)', display: 'block' }} />
        </div>
      )}
      <div className="p-6 text-center space-y-1" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
        {popup.title && <h2 className="text-2xl font-black leading-tight text-white drop-shadow">{popup.title}</h2>}
      </div>
      <div className="p-5 space-y-3 text-center">
        {popup.content && <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{popup.content}</p>}
        {popup.button_text && (
          <button onClick={onBtn} className="w-full py-3 rounded-xl font-bold text-white text-base shadow-lg hover:opacity-90 transition-opacity"
            style={{ background: accent }}>
            {popup.button_text}
          </button>
        )}
        <FreqLabel popup={popup} dontShow={dontShow} setDontShow={setDontShow} />
      </div>
    </div>
  );
};

// 3. Notice — clean minimal, text only
const NoticeTemplate = ({ popup, onClose, onBtn, dontShow, setDontShow }) => {
  const accent = popup.accent_color || '#2563eb';
  return (
    <div className="bg-white w-full overflow-hidden">
      <div className="h-1.5 w-full" style={{ background: accent }} />
      <div className="p-6 space-y-4">
        <CloseBtn onClick={onClose} />
        <div className="flex items-start gap-3 pr-6">
          <div className="w-2 h-full rounded-full flex-shrink-0 mt-1" style={{ background: accent, minHeight: '40px' }} />
          <div className="space-y-2">
            {popup.title && <h2 className="text-lg font-bold text-gray-900">{popup.title}</h2>}
            {popup.content && <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{popup.content}</p>}
          </div>
        </div>
        {popup.button_text && (
          <button onClick={onBtn} className="w-full py-2.5 rounded-lg font-semibold text-white text-sm hover:opacity-90 transition-opacity"
            style={{ background: accent }}>
            {popup.button_text}
          </button>
        )}
        <FreqLabel popup={popup} dontShow={dontShow} setDontShow={setDontShow} />
      </div>
    </div>
  );
};

// 4. WhatsApp — full image top, green theme below
const WhatsAppTemplate = ({ popup, onClose, onBtn, dontShow, setDontShow, onImageLoad }) => {
  const img = resolveImageUrl(popup.image_url);

  const handleImgLoad = (e) => {
    if (onImageLoad) onImageLoad(e.target.naturalWidth, e.target.naturalHeight);
  };

  return (
    <div className="bg-white w-full overflow-hidden">
      <CloseBtn onClick={onClose} />
      {img && (
        <div className="w-full flex justify-center overflow-hidden bg-gray-50">
          <img src={img} alt="" onLoad={handleImgLoad}
            style={{ width: 'auto', maxWidth: '100%', height: 'auto', maxHeight: 'min(55dvh, 55vh)', display: 'block' }} />
        </div>
      )}
      <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 text-white text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <MessageCircle className="h-6 w-6" />
          <span className="text-lg font-bold">WhatsApp</span>
        </div>
        {popup.title && <h2 className="text-lg font-bold">{popup.title}</h2>}
      </div>
      <div className="p-5 space-y-3">
        {popup.content && <p className="text-gray-600 text-sm leading-relaxed text-center whitespace-pre-line">{popup.content}</p>}
        {popup.button_text && (
          <button onClick={onBtn}
            className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 transition-colors">
            <MessageCircle className="h-5 w-5" />
            {popup.button_text}
          </button>
        )}
        <FreqLabel popup={popup} dontShow={dontShow} setDontShow={setDontShow} />
      </div>
    </div>
  );
};

// 5. Banner — full image with text overlay, auto aspect ratio from natural dimensions
const BannerTemplate = ({ popup, onClose, onBtn, dontShow, setDontShow, onImageLoad }) => {
  const img = resolveImageUrl(popup.image_url);
  const accent = popup.accent_color || '#2563eb';
  const [naturalRatio, setNaturalRatio] = useState(null);

  const handleImgLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setNaturalRatio(naturalWidth / naturalHeight);
    if (onImageLoad) onImageLoad(naturalWidth, naturalHeight);
  };

  return (
    <div className="bg-white w-full overflow-hidden">
      <div className="relative">
        {img
          ? <div className="w-full overflow-hidden"
                style={{ aspectRatio: naturalRatio ? String(naturalRatio) : 'auto', maxHeight: 'min(70dvh, 70vh)' }}>
              <img src={img} alt="" onLoad={handleImgLoad} className="w-full h-full object-cover" />
            </div>
          : <div className="w-full h-40" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }} />
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-5">
          <CloseBtn onClick={onClose} dark />
          {popup.title && <h2 className="text-white text-2xl font-black drop-shadow mb-1">{popup.title}</h2>}
          {popup.content && <p className="text-white/90 text-sm line-clamp-2 mb-3">{popup.content}</p>}
          {popup.button_text && (
            <button onClick={onBtn} className="self-start px-5 py-2 rounded-lg font-bold text-white text-sm hover:opacity-90"
              style={{ background: accent }}>
              {popup.button_text}
            </button>
          )}
        </div>
      </div>
      <div className="px-5 py-3 bg-gray-50">
        <FreqLabel popup={popup} dontShow={dontShow} setDontShow={setDontShow} />
      </div>
    </div>
  );
};

const SIZE_STYLE = {
  sm: { width: 'min(460px, 90vw)' },
  md: { width: 'min(620px, 92vw)' },
  lg: { width: 'min(820px, 94vw)' },
  xl: { width: 'min(1020px, 96vw)' },
};
const TEMPLATES = { classic: ClassicTemplate, offer: OfferTemplate, notice: NoticeTemplate, whatsapp: WhatsAppTemplate, banner: BannerTemplate };

// ── Mobile slide-up bar (shared) ─────────────────────────────
const MobileBar = ({ popup, onClose, onBtn, dontShow, setDontShow }) => {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 16);
    return () => clearTimeout(t);
  }, []);

  const img = resolveImageUrl(popup.image_url);
  const accent = popup.accent_color || '#2563eb';
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999]"
      style={{
        transform: entered ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 300ms ease-out',
        willChange: 'transform',
      }}
    >
      <div className="bg-white border-t-4 rounded-t-2xl p-4" style={{ borderColor: accent, boxShadow: '0 -4px 20px rgba(0,0,0,0.12)' }}>
        <button onClick={onClose} className="absolute top-3 right-3 rounded-full p-1 hover:bg-gray-100">
          <X className="h-4 w-4 text-gray-500" />
        </button>
        <div className="flex gap-3 items-center pr-8">
          {img && <img src={img} alt="" className="w-14 h-14 object-contain rounded-lg flex-shrink-0 bg-gray-50" />}
          <div className="flex-1 min-w-0">
            {popup.title && <p className="font-bold text-sm">{popup.title}</p>}
            {popup.content && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{popup.content}</p>}
            {popup.button_text && (
              <button onClick={onBtn} className="mt-2 px-4 py-1.5 rounded-lg text-white text-xs font-semibold hover:opacity-90" style={{ background: accent }}>
                {popup.button_text}
              </button>
            )}
          </div>
        </div>
        <div className="mt-2">
          <FreqLabel popup={popup} dontShow={dontShow} setDontShow={setDontShow} />
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────
const PopupModal = () => {
  const location = useLocation();
  const [queue, setQueue] = useState([]);
  const [queueIdx, setQueueIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dontShow, setDontShow] = useState(false);
  const [dynamicWidth, setDynamicWidth] = useState(null);
  const triggeredRef = useRef(false);
  const popupRef = useRef(null);
  const queueAdvanceRef = useRef(false);

  const popup = queue[queueIdx] ?? null;
  popupRef.current = popup;

  // Reset dynamic width whenever the displayed popup changes
  const popupId = popup?.id;
  useEffect(() => {
    setDynamicWidth(null);
  }, [popupId]);

  useEffect(() => {
    setQueue([]);
    setQueueIdx(0);
    setVisible(false);
    triggeredRef.current = false;

    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/popups/active`);
        const list = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        if (!list.length) return;
        if (location.pathname.startsWith('/admin')) return;
        const filtered = list.filter(p => {
          const pages = p.target_pages || [];
          if (pages.length > 0 && !pages.includes(location.pathname)) return false;
          return shouldShow(p);
        });
        setQueue(filtered);
      } catch { }
    };
    fetch();
  }, [location.pathname]);

  // Called by ClassicTemplate when image loads — adjusts popup width for portrait on desktop
  const handleImageLoad = useCallback((naturalWidth, naturalHeight) => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) return; // mobile: keep 96vw, scroll if needed

    const ratio = naturalWidth / naturalHeight;
    if (ratio >= 1) {
      // Landscape: no adjustment needed
      setDynamicWidth(null);
    } else {
      // Portrait on desktop: narrow popup to match image width at max-height
      const maxImgHeightPx = window.innerHeight * 0.65;
      const idealWidthPx = Math.round(maxImgHeightPx * ratio);
      const minWidth = 300; // ensure text is readable
      const maxScreen = Math.round(window.innerWidth * 0.92);
      setDynamicWidth(`${Math.min(Math.max(idealWidthPx, minWidth), maxScreen)}px`);
    }
  }, []);

  const show = useCallback(() => {
    const p = popupRef.current;
    if (triggeredRef.current || !p) return;
    triggeredRef.current = true;
    setVisible(true);
    axios.post(`${API_URL}/popups/${p.id}/impression`).catch(() => {});
  }, []);

  useEffect(() => {
    if (!popup) return;
    triggeredRef.current = false;
    const isAdvance = queueAdvanceRef.current;
    queueAdvanceRef.current = false;
    const trigger = popup.trigger_type || 'delay';
    if (trigger === 'delay' || isAdvance) {
      const delay = isAdvance ? 500 : (popup.delay_seconds ?? 2) * 1000;
      const t = setTimeout(show, delay);
      return () => clearTimeout(t);
    }
    if (trigger === 'scroll') {
      const pct = popup.scroll_percentage ?? 50;
      const onScroll = () => {
        const scrolled = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        if (scrolled >= pct) show();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }
    if (trigger === 'exit') {
      const onLeave = (e) => { if (e.clientY <= 0) show(); };
      document.addEventListener('mouseleave', onLeave);
      return () => document.removeEventListener('mouseleave', onLeave);
    }
  }, [popup, show]);

  const handleClose = () => {
    if (popup && dontShow) dismiss(popup, dontShow);
    setVisible(false);
    setDontShow(false);
    const nextIdx = queueIdx + 1;
    if (nextIdx < queue.length) {
      queueAdvanceRef.current = true;
      setQueueIdx(nextIdx);
      triggeredRef.current = false;
    }
  };

  const handleBtn = () => {
    const p = popupRef.current;
    if (p) {
      axios.post(`${API_URL}/popups/${p.id}/click`).catch(() => {});
      if (p.button_url) window.open(p.button_url, '_blank');
    }
    if (p && dontShow) dismiss(p, true);
    else if (p) dismiss(p, false);
    setVisible(false);
    setDontShow(false);
    const nextIdx = queueIdx + 1;
    if (nextIdx < queue.length) {
      queueAdvanceRef.current = true;
      setQueueIdx(nextIdx);
      triggeredRef.current = false;
    }
  };

  if (!visible || !popup) return null;

  const Template = TEMPLATES[popup.template] || ClassicTemplate;
  const isMobile = window.innerWidth < 768;
  const baseSize = isMobile ? { width: '96vw' } : (SIZE_STYLE[popup.popup_size] || SIZE_STYLE.lg);
  const containerStyle = {
    ...(dynamicWidth ? { width: dynamicWidth } : baseSize),
    maxHeight: 'min(92dvh, 92vh)',
    transition: 'width 250ms ease-out',
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-3">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative animate-in fade-in zoom-in-95 duration-200 overflow-y-auto rounded-2xl" style={containerStyle}>
        <Template
          popup={popup}
          onClose={handleClose}
          onBtn={handleBtn}
          dontShow={dontShow}
          setDontShow={setDontShow}
          onImageLoad={popup.template !== 'notice' ? handleImageLoad : undefined}
        />
      </div>
    </div>
  );
};

export default PopupModal;
